import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { Logger } from '../../../infrastructure/logging/logger';
import { ColorProp, getColor } from '../../../layout/Colors';
import { NavService } from '../../../layout/nav/nav.service';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { DataTableColumn } from '../../../shared/components/data-table/DataTableColumn.model';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { FilterParams, SortParams } from '../../../shared/search/SearchParams';
import { toTitleCase } from '../../../utilities/StringUtility';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { UpdatePolicyToolDialogComponent } from '../../policies/components/update-policy-tool-dialog/update-policy-tool-dialog.component';
import { Policy } from '../../policies/models/Policy';
import { PolicyService } from '../../policies/services/policy.service';
import { Property } from '../models/Property';
import { PropertyApiService } from '../services/property.api.service';
import { PropertiesFilterStatusComponent } from './components/filters/properties-filter-status/properties-filter-status.component';
import { UpdatePropertyStatusDialogComponent } from './components/update-property-status-dialog/update-property-status-dialog.component';
import { PropertyTableRow } from './models/PropertyTableRow.model';
import { PropertiesFilterStateService } from './services/properties-filter-state.service';
import { PropertiesMessageService } from './services/properties-table-message.service';

@Component({
  selector: 'app-properties',
  templateUrl: './properties.component.html',
  styleUrls: ['./properties.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    MatIcon, 
    MatCard, 
    DataTableComponent, 
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ]
})
export class PropertiesComponent implements OnInit {

  STATUS_ACTIVE = 1;
  STATUS_INACTIVE = 2;

  filterDefinitions: DataTableFilter[] = [
    { id: 'property-filter-status', name: 'Property Status', shortName: 'Status', component: PropertiesFilterStatusComponent }
  ];

  displayedColumns: DataTableColumn[] = [
    { column: 'id', display: 'Safely Prop. ID' },
    { column: 'referenceId', display: 'Property ID' },
    { column: 'organizationId', display: 'Organization ID' },
    { column: 'organizationName', display: 'Organization Name' },
    { column: 'name', display: 'Property Name' },
    { column: 'city', display: 'City/Town' },
    { column: 'state', display: 'State/Province' },
    { column: 'postalCode', display: 'Postal Code' },
    { column: 'pillStatus', display: 'Status' }
  ]

  tableToApiColumnMap: { [key: string]: string } = {
    'id': 'propertyId',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'name': 'name',
    'city': 'city',
    'state': 'state',
    'postalCode': 'postalCode',
    'pillStatus': 'status'
  }

  apiToTableColumnMap: { [key: string]: string } = {
    'propertyId': 'id',
    'referenceId': 'referenceId',
    'organizationId': 'organizationId',
    'organizationName': 'organizationName',
    'name': 'name',
    'city': 'city',
    'state': 'state',
    'postalCode': 'postalCode',
    'status': 'pillStatus'
  }

  @Input() rowClicked?: (row: any) => void;

  dataSource = new MatTableDataSource<PropertyTableRow>();
  mappedProperties: PropertyTableRow[] = [];
  createPropertyEnabled = false;
  loading = new BehaviorSubject<boolean>(false);
  tableLength = 0;
  pageSize = 10;
  pageNumber!: number;
  keyword: string = '';

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  organizationId!: any;
  selectedOrganization!: any;
  selectedOrganizationName: string = '';
  propertyStatusUpdate: boolean = false;
  propertiesToUpdate: any[] = [];
  availablePolicies: Policy[] = [];
  totalPropertyRecords: number = 0;
  shouldShowRemovePolicyButton: boolean = false;
  sharedPropertyPolicies: any;
  propertyHasPrimaryPolicy: boolean = false;

  constructor(
    private router: Router,
    private propertiesFilterStateService: PropertiesFilterStateService,
    private propertiesMessageService: PropertiesMessageService,
    private propertiesService: PropertyApiService,
    private searchService: SearchService,
    private organizationService: OrganizationService,
    private policyService: PolicyService,
    private logService: Logger,
    private navService: NavService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.setSearchQuery();
    this.setupFilterEventHandler();
    this.setupPagingState();
    this.setupFilterState();
    this.setupSearchTextState();
    this.setupSortState();

    if (this.navService.selectedOrgId.value > 0) {
      this.onOrganizationSearchEvent(this.navService.selectedOrgId.value);
    }

    this.navService.selectedOrg$.subscribe((org: any) => {
      if (org?.organization_id) {
        this.selectedOrganization = org;
        this.organizationId = org.organization_id;
        this.onOrganizationSearchEvent(org.organization_id);
      } else {
        this.selectedOrganization = undefined;
        this.selectedOrganizationName = '';
        this.loadPage();
      }
    });
  }

  setSearchQuery() {
    if (this.searchService.currentQuery.sortParams?.sortField === 'propertyId') {
      if (this.searchService.currentQuery.page > 1) {
        this.searchService.setPage(this.searchService.currentQuery.page);
        this.pageNumber = this.searchService.currentQuery.page;
        this.initialPageNumber = new BehaviorSubject<number>(this.searchService.currentQuery.page);
      } else {
        this.searchService.setPage(1);
        this.pageNumber = 1;
      }
    } else {
      this.searchService.resetQuery();
    }
    this.searchService.setSort("propertyId", -1);
  }

  private setupPagingState() {
    const pageSize = this.propertiesService.lastSearchRequest?.pageSize;
    if (pageSize) {
      this.searchService.setPageSize(pageSize);
      this.pageSize = pageSize;
      this.initialPageSize.next(pageSize);
    }
  }

  private setupFilterState() {
    if(this.propertiesService.lastSearchRequest?.filterParams?.find(f => f.fieldName === 'status')) {
      this.handleStatusFilterUpdate(Number(this.propertiesFilterStateService.status));
    }
  }

  private setupSearchTextState() {
    if(this.propertiesService.lastSearchRequest?.freeText) {
      this.addKeywordToQuery(this.propertiesService.lastSearchRequest.freeText);
      this.initialKeyword.next(this.propertiesService.lastSearchRequest.freeText);
    }
  }

  private setupSortState() {
    const sort = this.propertiesService.lastSearchRequest?.sortParams;
    if(sort) {
      this.searchService.addSort(sort);
      const sortField = this.getSortPropertyNameForTable(sort.sortField);
      const sortDirection = sort.sortOrder == 1 ? 'asc' : 'desc';
      this.initialSort.next({ active: sortField, direction: sortDirection });
    } else {
      this.initialSort.next({ active: 'id', direction: 'desc' });
    }
  }

  private setupFilterEventHandler() {
    this.propertiesMessageService.applyFilterEvent.subscribe(_ => {
      const status = this.propertiesFilterStateService.status;
      this.handleStatusFilterUpdate(Number(status));
      this.loadPage();
    });
  }

  private async handleStatusFilterUpdate(status: number) {
    switch(status) {
      case this.STATUS_ACTIVE:
        this.updateStatusFilter('Active');
        break;
      case this.STATUS_INACTIVE:
        this.updateStatusFilter('Inactive');
        break;
      default:
        this.searchService.removeFilter('status');
        break;
    }
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      const { properties, totalRecords } = await this.propertiesService.searchProperties(this.searchService.currentQuery);
      this.totalPropertyRecords = totalRecords;
      this.mappedProperties = properties.map(p => this.buildPropertyTableRow(p));
      this.updateTableDataSource(this.mappedProperties, totalRecords);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Properties', 'Error while retrieving Properties');
    } finally {
      this.loading.next(false);
    }
  }

  updateTableDataSource(properties: PropertyTableRow[], totalRecords: number) {
    if (this.selectedOrganization) {
      this.selectedOrganizationName = this.selectedOrganization.organization_name;
    } else {
      this.selectedOrganizationName = '';
    }
    this.dataSource = new MatTableDataSource(properties);
    this.tableLength = totalRecords;
  }

  buildPropertyTableRow(property: any): PropertyTableRow {
    return {
      id: property.propertyId,
      organizationId: property.organizationId,
      organizationName: property.organizationName,
      referenceId: property.referenceId,
      name: property.name,
      city: property.city,
      state: property.state,
      postalCode: property.postalCode,
      pillStatus: toTitleCase(property.status)
    }
  }

  updateStatusFilter(status: string) {
    const filter: FilterParams = {
      fieldName: 'status',
      value: [status],
      range: false
    };
    this.searchService.replaceFilter(filter);
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string): void {
    this.addKeywordToQuery(keyword);
    this.loadPage();
  }

  private addKeywordToQuery(keyword: string) {
    this.keyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
  }

  onOrganizationSearchEvent(organizationId: number) {
    this.searchService.setOrganizationId(organizationId);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onSortEvent(sortParams: SortParams) {
    this.addSortToQuery(sortParams);
    this.loadPage();
  }

  private addSortToQuery(sortParams: SortParams) {
    sortParams.sortField = this.tableToApiColumnMap[sortParams.sortField];
    this.searchService.addSort(sortParams);
  }

  private getSortPropertyNameForTable(field: string) {
    return this.apiToTableColumnMap[field];
  }

  getPropertyStatusColor(status: string): string {
    if(status?.toLowerCase() === 'active') {
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }

  onNewPropertyClick(): void {
    this.router.navigate(['/properties/form']);
  }

  getSelectedPropertyDetails(properties: any[]) {
    this.propertiesToUpdate = [];

    let ctr = 0;
    properties.forEach(property => {
      this.propertiesService.getPropertyDetail(property.id).then(prop => {
        ctr++;
        this.propertiesToUpdate.push(prop);
        if (ctr === properties.length && this.propertyStatusUpdate) {
          this.openUpdatePropertyStatusDialog();
        }
        if (ctr === properties.length && !this.propertyStatusUpdate) {
          this.checkPropertyPolicies(properties);
        }
      });
    });
  }

  checkPropertyPolicies(properties: any) {
    this.sharedPropertyPolicies = [];
    let allPropertiesPolicies: any = [];
    let ctr = 0;
    properties.forEach((prop: any) => {
      this.propertiesService.getPropertyPolicies(prop.id).then(policyArr => {
        ctr++;
        allPropertiesPolicies.push(policyArr);
        if (ctr === properties.length) {
          let activePrimarySharedPolicies = allPropertiesPolicies.flat().filter((policy: any) => policy.is_active === 1 && policy.status_id === 1);
          const idCounts = activePrimarySharedPolicies.reduce((acc: any, item: any) => {
            acc[item.insurance_policy_id] = (acc[item.insurance_policy_id] || 0) + 1;
            return acc;
          }, {});
          const duplicateIds = Object.keys(idCounts).filter(id => idCounts[id] > 1).map(Number);

          if (duplicateIds.length > 0) {
            this.shouldShowRemovePolicyButton = true;
            this.sharedPropertyPolicies = activePrimarySharedPolicies.filter((policy: any) => duplicateIds.includes(policy.insurance_policy_id));
          } else {
            this.shouldShowRemovePolicyButton = false;
          }
          this.propertyHasPrimaryPolicy = properties.every((prop: any) =>
            activePrimarySharedPolicies.some((policy: any) => policy.property_id === prop.id && policy.is_primary === 1)
          );
          this.getOrganizationPolicies(properties[0].organizationId);
        }
      });
    });
  }

  onPropertyUpdateStatusEvent(row: any) {
    this.propertyStatusUpdate = true;
    let propertiesToUpdate = row.selected;
    this.getSelectedPropertyDetails(propertiesToUpdate);
  }

  openUpdatePropertyStatusDialog() {
    const statusDialogRef = this.dialog.open(UpdatePropertyStatusDialogComponent, {
      width: '55%',
      maxHeight: '600px',
      disableClose: true,
      data: this.propertiesToUpdate
    });
    statusDialogRef.afterClosed().subscribe((statusId: number) => {
      if (statusId > 0) {
        try {
          this.propertiesToUpdate.forEach(property => {
            property.status_id = statusId;
            this.propertiesService.updatePropertyStatus(property).then(_ => {
              this.loadPage();
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Property status', 'Error while updating Property status');
        }
      }
    });
  }

  async onPropertyUpdatePolicyEvent(row: any) {
    this.propertyStatusUpdate = false;
    let propertiesToUpdate = row.selected;
    await this.getSelectedPropertyDetails(propertiesToUpdate);
  }

  async getOrganizationPolicies(organizationId: number) {
    if (organizationId) {
      const organizationPolicyResponse: any[] = await this.organizationService.getPoliciesByOrganizationId(organizationId);
      this.availablePolicies = organizationPolicyResponse.filter(policy => policy.status_id === 1);
      this.openUpdatePropertyPolicyDialog();
    }
  }

  openUpdatePropertyPolicyDialog() {
    const assignDialogRef = this.dialog.open(UpdatePolicyToolDialogComponent, {
      width: '90%',
      maxHeight: '800px',
      disableClose: true,
      data: { 
        policies: this.availablePolicies,
        component: 'properties',
        showRemovePolicyButton: this.shouldShowRemovePolicyButton,
        propertyHasPrimaryPolicy: this.propertyHasPrimaryPolicy,
        sharedPropertyPolicies: this.sharedPropertyPolicies,
        propertiesToUpdate: this.propertiesToUpdate
      }
    });
    assignDialogRef.afterClosed().subscribe(data => {
      if (data.selectedPolicyId > 0) {
        let policySelected = this.availablePolicies.find(policy => policy.insurance_policy_id === data.selectedPolicyId);

        try {
          this.propertiesToUpdate.forEach(property => {
            this.propertiesService.getPropertyPolicies(property.property_id.toString()).then(propertyPolicies => {
              let propertyPolicyCheck = propertyPolicies.some(policy => policy.insurance_policy_id === policySelected?.insurance_policy_id);

              if (propertyPolicyCheck && data.setPolicyAsPrimary) {
                let policyToMakePrimary = propertyPolicies.find(policy => policy.insurance_policy_id === policySelected?.insurance_policy_id);

                if (!policyToMakePrimary?.is_primary) {
                  this.makeExistingPolicyPrimary(policyToMakePrimary);
                }
              }

              if (!propertyPolicyCheck) {
                const propertyPolicyPayload = this.buildPropertyPolicy(policySelected, property);

                this.policyService.createPolicyByComponent(propertyPolicyPayload, 'properties').then(_ => {
                  if (data.setPolicyAsPrimary) {
                    this.removeAnyExistingPrimaryPropertyPolicies(property, policySelected);
                  }
                });
              }
            });
          });
        } catch (e) {
          this.logService.handleError(e, 'An error occurred while updating Property Policy', 'Error while updating Property Policy');
        }
      }
    });
  }

  makeExistingPolicyPrimary(policy: any) {
    let payload = {
      id: policy.id,
      is_primary: 1,
      keep_history: 0
    }
    this.policyService.updatePolicyByComponent(policy.id, payload, 'properties');
  }

  removeAnyExistingPrimaryPropertyPolicies(property: any, selectedPolicy: any) {
    this.propertiesService.getPropertyPolicies(property.property_id).then(propertyPolicies => {
      let anyPrimaryPolicyCheck = propertyPolicies.some(policy => policy.is_primary);

      let ctr = 0;
      propertyPolicies.forEach(policy => {
        ctr++;
        if (policy.insurance_policy_id === selectedPolicy.insurance_policy_id) {
          let payload = {
            id: policy.id,
            is_primary: 1,
            keep_history: 0
          }
          this.policyService.updatePolicyByComponent(policy.id, payload, 'properties');
        }
        if (anyPrimaryPolicyCheck && policy.is_primary && policy.insurance_policy_id != selectedPolicy.insurance_policy_id) {
          let payload = {
            id: policy.id,
            is_primary: 0,
            keep_history: 0
          }
          this.policyService.updatePolicyByComponent(policy.id, payload, 'properties');
        }

        if (ctr === propertyPolicies.length) {
          this.propertiesService.getPropertyPolicies(property.property_id);
        }
      });
    });
  }

  buildPropertyPolicy(selectedPolicy: any, property: Property) {
    let payload = {
      insurance_policy_id: selectedPolicy.policy.id.toString(),
      organization_id: property.organization_id,
      policy_price_id: selectedPolicy.policy_price_id,
      property_id: property.property_id.toString(),
      status_id: 1
    }
    return payload;
  }
}